import { useMemo, useState } from "react";
import { type ColumnDef } from "@tanstack/react-table";
import { IconArrowsMaximize, IconX } from "@tabler/icons-react";

import type { BrandAnalyticsResponse } from "@/services/brand.service";
import type { Program } from "@/types/program";
import type { DashboardCountry, DashboardProduct, StatValue } from "@/types/dashboard";
import { Dialog } from "@/components/ui/dialog";
import { DataTable } from "@/components/ui/data-table";
import { formatAmount, formatNumber } from "@/utils/formatters";
import { DashboardCard } from "@/components/dashboard/dashboard-card";
import { AffiliateMap, Flag } from "./affiliate-map";
import { StatGrid } from "@/components/dashboard/stat-grid";
import { countryName } from "./country-geometry";

function Row({
  id,
  label,
  value,
  note,
}: {
  id?: string;
  label: string;
  value: string;
  note?: string;
}) {
  return (
    <div
      id={id}
      className="flex items-baseline justify-between gap-4 border-t border-border py-3 first:border-t-0"
    >
      <div className="text-[13px] font-medium text-foreground">{label}</div>
      <div className="flex items-baseline gap-2">
        {note && <span className="text-xs text-muted-foreground">{note}</span>}
        <span className="text-lg font-semibold tabular-nums tracking-tight text-foreground">
          {value}
        </span>
      </div>
    </div>
  );
}

const countryColumns: ColumnDef<DashboardCountry>[] = [
  {
    accessorKey: "country",
    header: "Country",
    meta: { width: "w-48" },
    cell: ({ row }) => (
      <span className="flex items-center gap-2 font-medium text-foreground">
        <Flag code={row.original.country} />
        <span className="truncate">{countryName(row.original.country)}</span>
      </span>
    ),
  },
  {
    accessorKey: "affiliates",
    header: "Affiliates",
    meta: { width: "w-24" },
    cell: ({ row }) => <span className="tabular-nums">{formatNumber(row.original.affiliates)}</span>,
  },
  {
    accessorKey: "sellers",
    header: "Selling",
    meta: { width: "w-20" },
    cell: ({ row }) => <span className="tabular-nums">{formatNumber(row.original.sellers)}</span>,
  },
  {
    accessorKey: "orders",
    header: "Orders",
    meta: { width: "w-20" },
    cell: ({ row }) => <span className="tabular-nums">{formatNumber(row.original.orders)}</span>,
  },
  {
    accessorKey: "revenue",
    header: "Revenue",
    meta: { width: "w-28" },
    cell: ({ row }) => (
      <span className="tabular-nums font-medium text-foreground">{formatAmount(row.original.revenue)}</span>
    ),
  },
  {
    accessorKey: "commission",
    header: "Commission",
    meta: { width: "w-28" },
    cell: ({ row }) => <span className="tabular-nums">{formatAmount(row.original.commission)}</span>,
  },
  {
    id: "topProduct",
    header: "Top product",
    meta: { width: "w-48" },
    cell: ({ row }) =>
      row.original.topProduct ? (
        <span className="truncate" title={row.original.topProduct.title}>
          {row.original.topProduct.title}{" "}
          <span className="text-muted-foreground">({formatNumber(row.original.topProduct.units)})</span>
        </span>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
];

/** Where the affiliate network is, and what it can join. Click the expand
    control for the full map with every country's figures. */
export function NetworkCard({
  analytics,
  programs,
  countries,
  stats,
  topProduct = null,
  periodDays,
}: {
  analytics: BrandAnalyticsResponse | null;
  programs: Program[] | undefined;
  countries: DashboardCountry[] | undefined;
  stats?: StatValue[];
  /** The period's best-selling product, shown in the expanded view. */
  topProduct?: DashboardProduct | null;
  periodDays: number;
}) {
  const [expanded, setExpanded] = useState(false);

  // Until the period tables arrive, place dots from the all-time distribution
  // so the map is never blank.
  const mapCountries = useMemo<DashboardCountry[]>(() => {
    if (countries && countries.length) return countries;
    const distribution: Record<string, number> =
      analytics?.countriesDistribution?.countriesDistribution || {};
    return Object.entries(distribution).map(([country, affiliates]) => ({
      country,
      affiliates: Number(affiliates) || 0,
      sellers: 0,
      revenue: 0,
      orders: 0,
      commission: 0,
      topProduct: null,
    }));
  }, [countries, analytics]);

  const affiliates =
    analytics?.countriesDistribution?.totalAffiliates ??
    analytics?.totalAffilates?.totalAffilates ??
    0;
  const countryCount = mapCountries.filter((c) => c.country !== "Unknown").length;
  const campaigns = analytics?.campaignStats;
  const activePrograms = (programs ?? []).filter((p) => p.status === "ACTIVE").length;
  const tableRows = useMemo(
    () => [...mapCountries].sort((a, b) => b.revenue - a.revenue || b.affiliates - a.affiliates),
    [mapCountries]
  );
  const expandedStats = useMemo<StatValue[] | undefined>(() => {
    if (!stats) return undefined;
    const global: StatValue = {
      label: "Global top product",
      value: topProduct?.title ?? "—",
      kind: "text",
      note: topProduct ? `${formatNumber(topProduct.units)} units` : null,
      wide: true,
    };
    const selling: StatValue = {
      label: "Selling affiliates",
      value: mapCountries.reduce((s, c) => s + c.sellers, 0),
      kind: "number",
    };
    const revenue: StatValue = {
      label: "Revenue",
      value: mapCountries.reduce((s, c) => s + c.revenue, 0),
      kind: "currency",
    };
    return [...stats, selling, revenue, global];
  }, [stats, topProduct, mapCountries]);

  return (
    <>
      <DashboardCard
        id="dashboard-map-card"
        title="Network"
        stats={stats}
        fixed={
          <div className="px-3">
            {mapCountries.length > 0 ? (
              <AffiliateMap countries={mapCountries} onSelect={() => setExpanded(true)} />
            ) : (
              <div className="flex aspect-[2/1] items-center justify-center text-sm text-muted-foreground">
                No affiliates yet
              </div>
            )}
          </div>
        }
        action={
          <button
            type="button"
            onClick={() => setExpanded(true)}
            aria-label="Expand network map"
            title="Expand"
            className="inline-flex size-6 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <IconArrowsMaximize size={14} stroke={1.75} />
          </button>
        }
      >
        <div className="px-5 pb-2">
          <Row label="Affiliates" value={formatNumber(affiliates)} />
          <Row id="dashboard-map-countries-card" label="Countries" value={formatNumber(countryCount)} />
          <Row
            id="dashboard-programs-row"
            label="Programs"
            value={formatNumber(programs?.length ?? 0)}
            note={programs ? `${formatNumber(activePrograms)} active` : undefined}
          />
          <Row
            id="dashboard-campaign-card"
            label="Campaigns"
            value={formatNumber(campaigns?.totalCampaigns ?? 0)}
            note={`${formatNumber(campaigns?.activeCampaigns ?? 0)} active`}
          />
        </div>
      </DashboardCard>

      <Dialog open={expanded} onOpenChange={setExpanded} panelClassName="max-w-6xl">
        <div className="flex max-h-[90vh] flex-col">
          <div className="flex items-center justify-between border-b border-border px-5 py-3">
            <h2 className="text-[15px] font-semibold text-foreground">
              Affiliate network
              <span className="ml-2 text-xs font-normal text-muted-foreground">Last {periodDays} days</span>
            </h2>
            <button
              type="button"
              onClick={() => setExpanded(false)}
              aria-label="Close"
              className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <IconX size={18} stroke={1.75} />
            </button>
          </div>
          <div className="min-h-0 overflow-y-auto">
            <div className="grid gap-4 p-5 lg:grid-cols-[1fr_300px]">
              <AffiliateMap countries={mapCountries} className="rounded-lg border border-border" />
              <div className="overflow-hidden rounded-lg border border-border">
                {expandedStats && expandedStats.length ? (
                  <StatGrid stats={expandedStats} />
                ) : (
                  <div className="p-4 text-sm text-muted-foreground">Loading period figures</div>
                )}
              </div>
            </div>
            <div className="px-3 pb-3">
              <DataTable
                columns={countryColumns}
                data={tableRows}
                getRowId={(row) => row.country}
                emptyText="No affiliates yet"
              />
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
}
